import React from 'react';

import {
  Avatar,
  Button,
  Divider,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tag,
  Text,
  VStack,
  PopoverProps,
} from '@vimeo/bokeh';
import { VideosStack, PersonUser, UserLocation } from '@vimeo/bokeh-icons';
import { User } from '@vimeo/gctl-api/models/components/schemas/user/User';
import { translate } from '@vimeo/i18n-runtime';
import { PlayerContextProvider, PlayerType } from '@vimeo/player';

import { FollowButton } from './FollowButton';
import { LinkOrStaticElem } from './LinkOrStaticElem';
import MiniPlayer from './MiniPlayer';
import { T } from './translation';
import { getAvatarImages } from './utils/utils';

const CARD_WIDTH = 320;

interface UserCardProps extends PopoverProps {
  onProfileClick?: () => void;
  openDelay?: number;
  placement?: PopoverProps['placement'];
  profileUrl?: string;
  user: User;
  children: React.ReactNode;
  hasFollow?: boolean;
  followSource?: string;
}

export function UserCard({
  onProfileClick,
  openDelay,
  placement = 'top-start',
  profileUrl,
  hasFollow,
  followSource = '',
  user,
  children,
  ...rest
}: UserCardProps) {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const followersString = T.followersCount(user?.metadata?.connections?.followers?.total);
  const videosString = T.videosCount(user?.metadata?.publicVideos?.total);
  const isShowVideoString = typeof user?.metadata?.publicVideos?.total !== 'undefined';
  const skills = user?.skills ?? [];
  const [avaSrc, avaSrcSet] = getAvatarImages(user?.pictures?.sizes);
  const label = translate('Link to user profile');
  const backgroundVideo = user?.backgroundVideo?.[0];

  return (
    <Popover
      placement={placement}
      trigger="hover"
      openDelay={openDelay}
      onOpen={() => setIsPopoverOpen(true)}
      onClose={() => setIsPopoverOpen(false)}
      {...rest}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent w={CARD_WIDTH} borderRadius="drawer" p={0}>
        {backgroundVideo ? (
          <PlayerContextProvider
            type={PlayerType.BarebonePlayer}
            assetUrls={window.playerAssetUrls}
          >
            <PopoverHeader textStyle="heading-sm" display="flex" justifyContent="space-between">
              <MiniPlayer
                backgroundVideo={backgroundVideo}
                elemWidth={CARD_WIDTH}
                isVisible={isPopoverOpen}
              />
            </PopoverHeader>
          </PlayerContextProvider>
        ) : null}

        <PopoverBody px={50} pb={50}>
          <VStack w="100%" p={200} pt={400} spacing={200}>
            {/* Avatar */}
            <LinkOrStaticElem label={label} newTab onClick={onProfileClick} url={profileUrl}>
              <Avatar size="2xl" alt={user.name} src={avaSrc} srcSet={avaSrcSet} />
            </LinkOrStaticElem>

            {/* Name and location */}
            <VStack>
              <LinkOrStaticElem label={label} newTab onClick={onProfileClick} url={profileUrl}>
                <Text as="h4" variant="heading-md" align="center">
                  {user.name}
                </Text>
              </LinkOrStaticElem>
              {user.locationDetails?.formattedAddress && (
                <HStack spacing={50} textAlign="center">
                  <UserLocation color="text-secondary" />
                  <Text variant="body-md" color="text-secondary">
                    {user.locationDetails?.formattedAddress}
                  </Text>
                </HStack>
              )}
            </VStack>

            {/* Roles list */}
            {skills.length > 0 ? (
              <HStack wrap="wrap" justify="center">
                {skills.map((skill) => (
                  // TODO: Bokeh component library will eventually catch up with the design
                  // requirements. We should be able to remove these extensive style overrides
                  // when Bokeh has been updated.
                  <Tag
                    key={skill.name}
                    size="sm"
                    fontSize="text-sm"
                    fontWeight="regular"
                    cursor="default"
                    px={200}
                  >
                    {skill.name}
                  </Tag>
                ))}
              </HStack>
            ) : null}

            {/* Videos count and followers count */}
            <VStack w="100%" spacing={100}>
              <Divider borderColor="stroke" _dark={{ borderColor: 'stroke' }} />
              <HStack spacing={200}>
                {isShowVideoString ? (
                  <HStack spacing={75}>
                    <VideosStack />
                    <Text variant="body-xl" fontSize="text-sm">
                      {videosString}
                    </Text>
                  </HStack>
                ) : null}
                <HStack spacing={75}>
                  <PersonUser />
                  <Text variant="body-xl" fontSize="text-sm">
                    {followersString}
                  </Text>
                </HStack>
              </HStack>
              <Divider borderColor="stroke" _dark={{ borderColor: 'stroke' }} />
            </VStack>

            {/* Click button to view user profile */}
            {profileUrl ? (
              <Button
                as="a"
                href={profileUrl}
                rel="noopener noreferrer"
                target="_blank"
                aria-label={translate('View profile')}
                size="sm"
                w="100%"
                onClick={onProfileClick}
                variant="primary"
              >
                {translate('View profile')}
              </Button>
            ) : null}

            {hasFollow && user && <FollowButton user={user} source={followSource} />}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
